import {
  createContext,
  startTransition,
  useContext,
  useEffect,
  useId,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from 'react'
import { DayPicker, getDefaultClassNames, type DayButtonProps, type Matcher } from 'react-day-picker'
import {
  addDays,
  addMonths,
  differenceInCalendarDays,
  endOfMonth,
  format,
  isSameMonth,
  isValid,
  parseISO,
  startOfMonth,
  startOfToday,
} from 'date-fns'
import 'react-day-picker/style.css'
import './availability-range-picker.css'

import { dateFnsLocale } from '@/shared/lib/datetime'
import { cn } from '@/shared/lib/utils'
import { Button } from '@/shared/components/ui/button'
import { FrenchDateInput } from '@/shared/components/cse/FrenchDateInput'
import { Label } from '@/shared/components/ui/label'
import { Skeleton } from '@/shared/components/ui/skeleton'
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/shared/components/ui/sheet'
import {
  availabilityLegendOrder,
  hasVariableNightlyPrices,
  type AvailabilityCalendarDay,
  type AvailabilityLegendKey,
  type AvailabilityStatus,
} from './availabilityCalendarMeta'
import { buildSelectionRangeModifiers } from './buildSelectionRangeModifiers'
import { isPastAvailabilityDay } from './isPastAvailabilityDay'

// Binary visual status on the grid (detail stays on hover / aria - never color alone, RGAA).
// --cal-* vars (optional admin overrides) fall back to design tokens.
const availClass =
  '[&>button]:font-medium [&>button]:text-foreground [&>button]:bg-[color-mix(in_oklab,var(--cal-available)_18%,transparent)]'
const unavailableClass =
  '[&>button]:bg-[color:var(--cal-outside-window)] [&>button]:text-muted-foreground [&>button]:border-transparent'
/** Lead-time blocked: dashed border + cooler tint — must not read as plain unavailable grey. */
const leadTimeClass =
  '[&>button]:bg-[color-mix(in_oklab,var(--cal-lead-time)_45%,transparent)] [&>button]:text-muted-foreground [&>button]:border [&>button]:border-dashed [&>button]:border-[color:var(--cal-lead-time)]'
/** High-season closed: distinct fill + solid border (never ring) + asterisk in the day button. */
const seasonClosedClass =
  '[&>button]:bg-[color-mix(in_oklab,var(--cal-season-closed)_28%,transparent)] [&>button]:text-foreground [&>button]:border-[color:var(--cal-season-closed)] [&>button]:border-2'
/** Slot mode: the arrival (clickable) day - solid fill + bold border, stands out from both the period body and full/unavailable days. */
const slotArrivalClass =
  '[&>button]:!bg-[color-mix(in_oklab,var(--cal-available)_32%,transparent)] [&>button]:!border-2 [&>button]:!border-[color:var(--cal-available)] [&>button]:font-semibold'
/** Slot mode: bookable-but-not-arrival days inside the period - faint tint + dashed border, distinct from true full/unavailable days. */
const slotMidClass =
  'cal-slot-mid [&>button]:!bg-[color-mix(in_oklab,var(--cal-available)_8%,transparent)] [&>button]:!text-muted-foreground [&>button]:!opacity-70 [&>button]:!border [&>button]:!border-dashed [&>button]:!border-[color-mix(in_oklab,var(--cal-available)_45%,transparent)] [&>button]:![text-decoration:none]'

const modifierClassNames: Record<string, string> = {
  avail: availClass,
  low: availClass,
  full: unavailableClass,
  annotated: unavailableClass,
  seasonClosed: seasonClosedClass,
  outsideWindow: unavailableClass,
  leadTime: leadTimeClass,
  slotArrival: slotArrivalClass,
  slotMid: slotMidClass,
  // Alternating slot/period bands so adjacent availability periods are visually distinct.
  periodBandA:
    '[&>button]:bg-[color-mix(in_oklab,var(--cal-period-a)_22%,transparent)] [&>button]:border-b-4 [&>button]:border-b-[color:var(--cal-period-a)]',
  periodBandB:
    '[&>button]:bg-[color-mix(in_oklab,var(--cal-period-b)_22%,transparent)] [&>button]:border-b-4 [&>button]:border-b-[color:var(--cal-period-b)]',
  // CAL-A2: departure-only day - diagonal stripe (bookable as check-out, not as check-in).
  checkoutOnly:
    '[&>button]:[background-image:linear-gradient(135deg,transparent_46%,var(--color-border)_46%,var(--color-border)_54%,transparent_54%)]',
  selEdge:
    '[&>button]:!bg-primary [&>button]:!text-primary-foreground [&>button]:font-semibold [&>button]:[&_span:last-child]:!text-primary-foreground',
  selMiddle: '[&>button]:!bg-[var(--v2-brand-soft)] [&>button]:!text-foreground',
}

const legendSwatchBase =
  'mt-0.5 inline-block h-4 w-5 shrink-0 rounded-[var(--cal-tile-radius)] border'

const legendSwatch: Record<AvailabilityLegendKey, string> = {
  available: `${legendSwatchBase} border-[color:var(--cal-available)] bg-[color-mix(in_oklab,var(--cal-available)_18%,transparent)]`,
  unavailable: `${legendSwatchBase} border-transparent bg-[color:var(--cal-outside-window)]`,
}

const legendSeasonClosedSwatch = `${legendSwatchBase} border-2 border-[color:var(--cal-season-closed)] bg-[color-mix(in_oklab,var(--cal-season-closed)_28%,transparent)]`
const legendLeadTimeSwatch = `${legendSwatchBase} border border-dashed border-[color:var(--cal-lead-time)] bg-[color-mix(in_oklab,var(--cal-lead-time)_45%,transparent)]`
const legendPeriodASwatch = `${legendSwatchBase} border-b-4 border-b-[color:var(--cal-period-a)] bg-[color-mix(in_oklab,var(--cal-period-a)_22%,transparent)]`
const legendPeriodBSwatch = `${legendSwatchBase} border-b-4 border-b-[color:var(--cal-period-b)] bg-[color-mix(in_oklab,var(--cal-period-b)_22%,transparent)]`
const legendSlotArrivalSwatch = `${legendSwatchBase} border-2 border-[color:var(--cal-available)] bg-[color-mix(in_oklab,var(--cal-available)_32%,transparent)]`
const legendSlotMidSwatch = `${legendSwatchBase} border-dashed border-[color-mix(in_oklab,var(--cal-available)_45%,transparent)] bg-[color-mix(in_oklab,var(--cal-available)_8%,transparent)]`

/** Optional hex overrides from brand ui_preferences.calendar_colors (CR-14). */
export type CalendarColorOverrides = Partial<Record<AvailabilityStatus, string>>

function calendarColorStyle(colors: CalendarColorOverrides | undefined): CSSProperties {
  const pick = (key: AvailabilityStatus, fallback: string): string => {
    const hex = colors?.[key]
    return typeof hex === 'string' && /^#[0-9A-Fa-f]{6}$/.test(hex) ? hex : fallback
  }

  return {
    '--cal-available': pick('available', 'var(--color-primary)'),
    '--cal-low': pick('low', 'var(--color-warning)'),
    '--cal-full': pick('full', 'var(--color-destructive)'),
    '--cal-annotated': pick('annotated', 'var(--color-warning)'),
    '--cal-season-closed': pick('season_closed', 'var(--color-warning)'),
    '--cal-outside-window': pick(
      'outside_window',
      'color-mix(in oklab, var(--color-muted-foreground) 20%, transparent)',
    ),
    '--cal-lead-time': pick(
      'lead_time',
      'color-mix(in oklab, var(--color-muted-foreground) 35%, transparent)',
    ),
    '--cal-period-a': 'var(--color-primary)',
    '--cal-period-b': 'color-mix(in oklab, var(--color-primary) 45%, var(--color-warning))',
  } as CSSProperties
}

function calendarPickerStyle(colors: CalendarColorOverrides | undefined): CSSProperties {
  return {
    ...calendarColorStyle(colors),
    '--cal-tile-radius': '0.375rem',
    '--cal-tile-padding': '0.25rem',
    '--cal-cell-gap': '0.125rem',
    '--cal-month-gap': '1.5rem',
    '--rdp-day-height': '3.25rem',
    '--rdp-day-width': 'calc((100% - 6 * var(--cal-cell-gap)) / 7)',
    '--rdp-day_button-border-radius': 'var(--cal-tile-radius)',
    '--rdp-day_button-height': '100%',
    '--rdp-day_button-width': '100%',
    '--rdp-day_button-border': '1px solid transparent',
    '--rdp-selected-border': '0',
    '--rdp-disabled-opacity': '1',
    '--rdp-months-gap': 'var(--cal-month-gap)',
    /* Match size-11 chevrons so around-layout caption margins clear the buttons. */
    '--rdp-nav_button-width': '2.75rem',
    '--rdp-nav_button-height': '2.75rem',
    '--rdp-nav-height': '2.75rem',
  } as CSSProperties
}

export type CalendarMobileLayout = 'hybrid' | 'inline' | 'sheet'

function toDateOrNull(value: string | null): Date | null {
  if (value === null || value === '') return null
  const d = parseISO(value)
  return isValid(d) ? d : null
}

function iso(date: Date): string {
  return format(date, 'yyyy-MM-dd')
}

interface DayMeta {
  byIso: Map<string, AvailabilityCalendarDay>
  from: string
  to: string
  checkInAria: string
  checkOutAria: string
  /** ISO dates that are valid check-out endpoints but not check-in (CAL-A2). */
  checkoutOnly: Set<string>
  checkoutOnlyAria: string
  /** Nightly rate shown on bookable days; null when free or unset. */
  price: { short: string; aria: string } | null
  /** When false, hide all day prices (admin toggle). */
  pricesEnabled: boolean
  /** i18n template for per-day price aria when day has its own nightlyPriceShort. */
  dayPriceAria: (price: string) => string
  priceSizeClass: string
  /** Slot booking: only slotStartDates are clickable - a bookable non-start day reads as unavailable. */
  slotStartOnly: boolean
  slotStartDates: Set<string>
  slotArrivalAria: string
}

const DayMetaContext = createContext<DayMeta>({
  byIso: new Map(),
  from: '',
  to: '',
  checkInAria: '',
  checkOutAria: '',
  checkoutOnly: new Set(),
  checkoutOnlyAria: '',
  price: null,
  pricesEnabled: false,
  dayPriceAria: (price) => price,
  priceSizeClass: 'text-[0.62rem]',
  slotStartOnly: false,
  slotStartDates: new Set(),
  slotArrivalAria: '',
})

/**
 * Custom day button (module-level → stable identity, no remount): optional nightly rate under
 * bookable days, rich aria-label + `title` tooltip for the precise availability reason.
 * Preserves react-day-picker focus/click wiring by spreading the provided button props.
 */
function AvailabilityDayButton({ day, modifiers, ...buttonProps }: DayButtonProps) {
  const ref = useRef<HTMLButtonElement>(null)
  useEffect(() => {
    if (modifiers.focused) ref.current?.focus()
  }, [modifiers.focused])

  const ctx = useContext(DayMetaContext)
  const { children, ...rest } = buttonProps
  const isoDate = iso(day.date)
  const meta = ctx.byIso.get(isoDate)
  const bookable =
    (meta?.status === 'available' || meta?.status === 'low') &&
    (!ctx.slotStartOnly || ctx.slotStartDates.has(isoDate))
  const dayShort = meta?.nightlyPriceShort
  const price =
    bookable && ctx.pricesEnabled
      ? dayShort != null
        ? { short: dayShort, aria: ctx.dayPriceAria(dayShort) }
        : ctx.price
      : null

  const isSeasonClosed = meta?.status === 'season_closed'
  const parts = [format(day.date, 'EEEE d MMMM yyyy', { locale: dateFnsLocale() })]
  if (meta) parts.push(meta.label)
  if (price) parts.push(price.aria)
  if (ctx.checkoutOnly.has(isoDate)) parts.push(ctx.checkoutOnlyAria)
  if (ctx.slotStartOnly && ctx.slotArrivalAria !== '' && ctx.slotStartDates.has(isoDate)) {
    parts.push(ctx.slotArrivalAria)
  }
  if (isoDate === ctx.from) parts.push(ctx.checkInAria)
  else if (isoDate === ctx.to) parts.push(ctx.checkOutAria)

  const selected = isoDate === ctx.from || isoDate === ctx.to
  const isSelEdge = Boolean(modifiers.selEdge)

  return (
    <button
      ref={ref}
      {...rest}
      aria-label={parts.join(' - ')}
      aria-pressed={selected || undefined}
      title={meta?.label}
    >
      <span className="inline-flex items-baseline gap-0.5">
        <span>{children}</span>
        {isSeasonClosed ? (
          <span className="text-[0.7em] font-semibold leading-none" aria-hidden>
            *
          </span>
        ) : null}
      </span>
      {price ? (
        <span
          className={cn(
            ctx.priceSizeClass,
            'font-normal leading-none',
            isSelEdge ? 'text-primary-foreground' : 'text-muted-foreground',
          )}
        >
          {price.short}
        </span>
      ) : null}
    </button>
  )
}

const dayComponents = { DayButton: AvailabilityDayButton }

export interface AvailabilityRangePickerLabels {
  nights: (count: number) => string
  clear: string
  legendTitle: string
  legendHint?: string
  /** Binary legend labels (`available` / `unavailable`). */
  legend: (key: AvailabilityLegendKey) => string
  /** Accessible labels for month navigation chevrons. */
  prevMonth?: string
  nextMonthNav?: string
  pickHint: string
  /** Appended to a day's aria-label when it is the selected check-in / check-out. */
  checkInAria: string
  checkOutAria: string
  /** Live-region sentences announced when the selection changes. */
  announceCheckIn: (date: string) => string
  announceStay: (from: string, to: string, count: number) => string
  /** Min-stay hint shown while a check-in is open (CAL-A3). */
  minStayHint: (count: number) => string
  /** Typed date inputs (CAL-A5). */
  checkInLabel: string
  checkOutLabel: string
  /** Empty / error grid states (CAL-A6). */
  loadError: string
  retry: string
  emptyMonth: string
  nextMonth: string
  /** Appended to a departure-only day's aria-label (CAL-A2). */
  checkoutOnlyAria: string
  /** Slot mode: appended to an arrival day's aria-label. */
  slotArrivalAria?: string
  /** Legend row for checkout-only days when turnaround is on. */
  legendCheckoutOnly?: string
  /** Legend row when high-season closed days are present. */
  legendSeasonClosed?: string
  /** Legend row when portal lead-time blocked days are present. */
  legendLeadTime?: string
  /** Legend rows when ≥2 slot periods are visible (A / B bands). */
  legendPeriodA?: string
  legendPeriodB?: string
  /** Legend rows in slot-arrival mode: the clickable arrival day vs. the rest of the period. */
  legendSlotArrival?: string
  legendSlotMid?: string
  /** Per-day price aria when the day exposes its own nightlyPriceShort (CR-14-PRICE). */
  dayPriceAria: (price: string) => string
  /** Mobile bottom-sheet strings. */
  sheetTitle: string
  sheetTrigger: string
  sheetApply: string
  sheetClose: string
  /** RESA-F04 - jump the visible month back to today; hidden when already on today's month. */
  todayButton?: string
  /**
   * RESA-F04 - shown instead of the plain min-stay hint when the open check-in has fewer
   * consecutive bookable nights than the minimum stay, so every check-out day ends up
   * disabled (a silent dead end otherwise - nothing explains why nothing is clickable).
   */
  checkoutDeadEndHint?: (minNights: number) => string
}

export interface AvailabilityRangePickerProps {
  days: AvailabilityCalendarDay[]
  selectedFrom: string | null
  selectedTo: string | null
  onSelect: (range: { from: string | null; to: string | null }) => void
  onRangeChange: (from: Date, to: Date) => void
  onAnnotatedActivate?: (day: AvailabilityCalendarDay) => void
  /** Activating a season-closed day (high-season) → surface the wish flow (CAL-B2). */
  onSeasonBlockActivate?: (day: AvailabilityCalendarDay) => void
  /** CAL-B3 - full day → join waiting queue. */
  onFullActivate?: (day: AvailabilityCalendarDay) => void
  /** Availability request failed → show an error + retry (CAL-A6). */
  error?: boolean
  onRetry?: () => void
  minNights?: number
  maxNights?: number | null
  /** Max selectable / fetchable calendar span in days (admin reservation global settings). */
  maxSpanDays?: number
  lockedNights?: number | null
  /** Nightly rate rendered on each bookable day; null hides it (free or unset). */
  nightlyPrice?: { short: string; aria: string } | null
  /** When false, hide prices under days even if nightlyPrice is set (admin toggle). */
  showPrices?: boolean
  /** Fiche = md, wizard = sm. */
  priceSize?: 'md' | 'sm'
  /** Total stay price (nights × rate), pre-formatted; shown when a full range is set (CAL-A1). */
  stayTotalLabel?: string | null
  /** CAL-A2 turnaround / checkout-only half-days. Default true. */
  turnaroundEnabled?: boolean
  /**
   * Slot bookings are one indivisible period: only each period's first (arrival) day is
   * clickable, and a single click auto-selects the full period as check-out. Other bookable
   * days inside the period render as unavailable.
   */
  slotStartOnly?: boolean
  /** Mobile presentation: hybrid (sheet < md), inline, or always sheet. */
  mobileLayout?: CalendarMobileLayout
  /** Optional admin color overrides (brand ui_preferences.calendar_colors). */
  calendarColors?: CalendarColorOverrides
  loading?: boolean
  /** Optional recovery action displayed when the visible range has no bookable day. */
  emptyAction?: ReactNode
  ariaLabel: string
  labels: AvailabilityRangePickerLabels
  className?: string
}

/**
 * Interactive availability + range calendar (react-day-picker v10) - canonical CR-14
 * picker for property detail and reservation wizard. One object does two jobs: show
 * per-day availability and capture the stay (check-in → check-out).
 *
 * Two-click range with a hover preview of the pending span; unavailable days are disabled;
 * while a check-in is open, check-out dates that break the min/max-stay or span an
 * unavailable night are disabled too. Annotated days stay clickable to reveal their reason.
 * Mobile-first: a full-width bottom sheet on small screens, an inline two-month calendar
 * from `md`. Selection changes are announced in a polite live region (RGAA).
 */
export function AvailabilityRangePicker({
  days,
  selectedFrom,
  selectedTo,
  onSelect,
  onRangeChange,
  onAnnotatedActivate,
  onSeasonBlockActivate,
  onFullActivate,
  error = false,
  onRetry,
  minNights = 1,
  maxNights = null,
  maxSpanDays = 365,
  lockedNights = null,
  nightlyPrice = null,
  showPrices = true,
  priceSize = 'md',
  stayTotalLabel = null,
  turnaroundEnabled = true,
  slotStartOnly = false,
  mobileLayout = 'hybrid',
  calendarColors,
  loading = false,
  emptyAction,
  ariaLabel,
  labels,
  className,
}: AvailabilityRangePickerProps) {
  const today = startOfToday()
  const todayIso = iso(today)
  const safeDays = Array.isArray(days) ? days : []
  const effectiveMaxSpanDays = Math.max(1, Math.trunc(maxSpanDays ?? 365) || 365)
  const latestBookableDay = addDays(today, effectiveMaxSpanDays)
  const isBlock = typeof lockedNights === 'number' && lockedNights > 0
  const pricesEnabled = hasVariableNightlyPrices(safeDays) || Boolean(showPrices)
  const effectivePrice = pricesEnabled ? nightlyPrice : null
  const priceSizeClass = priceSize === 'sm' ? 'text-[0.55rem]' : 'text-[0.62rem]'
  const pickerStyle = calendarPickerStyle(calendarColors)
  const rdpClassNames = getDefaultClassNames()

  // React Compiler memoizes these; manual memoization is avoided because the Date-derived
  // values trip its "may be modified later" guard (preserve-manual-memoization).
  const dayByIso = new Map<string, AvailabilityCalendarDay>()
  for (const d of safeDays) {
    if (d?.date) dayByIso.set(d.date, d)
  }

  const isBookable = (d?: AvailabilityCalendarDay): boolean => d?.status === 'available' || d?.status === 'low'

  const statusDates = {
    avail: [] as Date[],
    low: [] as Date[],
    annotated: [] as Date[],
    full: [] as Date[],
    seasonClosed: [] as Date[],
    outsideWindow: [] as Date[],
    leadTime: [] as Date[],
    periodBandA: [] as Date[],
    periodBandB: [] as Date[],
    slotArrival: [] as Date[],
    slotMid: [] as Date[],
  }
  const slotOrder: string[] = []
  for (const d of safeDays) {
    if (
      !isPastAvailabilityDay(d.date, todayIso) &&
      (d.status === 'available' || d.status === 'low') &&
      d.slotId &&
      !slotOrder.includes(d.slotId)
    ) {
      slotOrder.push(d.slotId)
    }
  }
  const showPeriodBands = slotOrder.length >= 2
  const slotBandIndex = new Map(slotOrder.map((id, i) => [id, i % 2]))

  // Slot mode: each contiguous run of bookable nights sharing the same slotId is one
  // indivisible period. Only its first night (arrival) is clickable; the checkout is the
  // day after the run's last night.
  const slotStartDates = new Set<string>()
  const slotStartCheckout = new Map<string, string>()
  if (slotStartOnly) {
    const bookableSorted = safeDays
      .filter((d) => !isPastAvailabilityDay(d.date, todayIso) && isBookable(d) && d.slotId)
      .slice()
      .sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0))
    let i = 0
    while (i < bookableSorted.length) {
      const start = bookableSorted[i]
      let j = i
      while (
        j + 1 < bookableSorted.length &&
        bookableSorted[j + 1].slotId === start.slotId &&
        bookableSorted[j + 1].date === iso(addDays(parseISO(bookableSorted[j].date), 1))
      ) {
        j++
      }
      const last = bookableSorted[j]
      slotStartDates.add(start.date)
      slotStartCheckout.set(start.date, iso(addDays(parseISO(last.date), 1)))
      i = j + 1
    }
  }

  for (const d of safeDays) {
    const date = toDateOrNull(d.date)
    if (!date) continue
    if (isPastAvailabilityDay(d.date, todayIso)) {
      statusDates.outsideWindow.push(date)
      continue
    }
    const isBookableSlotMid = slotStartOnly && isBookable(d) && !slotStartDates.has(d.date)
    const isSlotArrival = slotStartOnly && isBookable(d) && slotStartDates.has(d.date)
    if (isBookableSlotMid) statusDates.slotMid.push(date)
    else if (isSlotArrival) {
      statusDates.slotArrival.push(date)
      if (d.status === 'available') statusDates.avail.push(date)
      else statusDates.low.push(date)
    } else if (d.status === 'available') statusDates.avail.push(date)
    else if (d.status === 'low') statusDates.low.push(date)
    else if (d.status === 'annotated') statusDates.annotated.push(date)
    else if (d.status === 'season_closed') statusDates.seasonClosed.push(date)
    else if (d.status === 'outside_window') statusDates.outsideWindow.push(date)
    else if (d.status === 'lead_time') statusDates.leadTime.push(date)
    else statusDates.full.push(date)

    if (showPeriodBands && !isBookableSlotMid && (d.status === 'available' || d.status === 'low') && d.slotId) {
      const band = slotBandIndex.get(d.slotId) ?? 0
      if (band === 0) statusDates.periodBandA.push(date)
      else statusDates.periodBandB.push(date)
    }
  }

  const hasSeasonClosed = statusDates.seasonClosed.length > 0
  const hasLeadTime = statusDates.leadTime.length > 0

  // CAL-A2: a full/blocked day that immediately follows a bookable night is a valid
  // check-out endpoint but not a check-in ("departure-only"), when turnaround is on.
  const checkoutOnly = new Set<string>()
  if (turnaroundEnabled) {
    for (const d of safeDays) {
      const dayDate = toDateOrNull(d.date)
      if (!dayDate || isBookable(dayByIso.get(d.date))) continue
      const prevIso = iso(addDays(dayDate, -1))
      if (isBookable(dayByIso.get(prevIso))) checkoutOnly.add(d.date)
    }
  }

  const fromDate = toDateOrNull(selectedFrom)
  const toDate = toDateOrNull(selectedTo)
  const nights = fromDate && toDate ? differenceInCalendarDays(toDate, fromDate) : 0
  const checkInSlotId = fromDate ? dayByIso.get(iso(fromDate))?.slotId : undefined

  const [hovered, setHovered] = useState<Date | null>(null)
  // Preview end while a check-in is open and the pointer is past it (desktop hover affordance).
  const previewTo =
    !isBlock && fromDate && !toDate && hovered && iso(hovered) > iso(fromDate) ? hovered : null

  /**
   * While a check-in is open (no check-out yet), the valid check-out window. Booking a stay
   * requires every night in `[check-in, check-out)` to be available; so the latest valid
   * check-out is the day after the last consecutive available night from the check-in.
   * Slot mode: stay within the same slot_id so cross-period selection cannot complete.
   */
  const checkoutBound = ((): { earliest: Date; latest: Date } | null => {
    if (isBlock || slotStartOnly || !fromDate || toDate) return null
    let cursor = fromDate
    while (true) {
      const d = dayByIso.get(iso(cursor))
      const bookable = d ? d.status === 'available' || d.status === 'low' : false
      if (!bookable) break
      if (checkInSlotId != null && d?.slotId != null && d.slotId !== checkInSlotId) break
      cursor = addDays(cursor, 1)
    }
    const maxByStay = maxNights != null ? addDays(fromDate, maxNights) : null
    const latest = maxByStay && maxByStay < cursor ? maxByStay : cursor
    return { earliest: addDays(fromDate, Math.max(1, minNights)), latest }
  })()
  // RESA-F04: fewer consecutive bookable nights than the minimum stay from this check-in
  // → every check-out day is disabled by the {before: earliest}/{after: latest} matchers
  // below (earliest > latest). Surface it instead of leaving the grid silently all-disabled.
  const isCheckoutDeadEnd = checkoutBound !== null && checkoutBound.latest < checkoutBound.earliest

  // Full days: disabled by default; checkout-only exception when completing an open stay (CAL-A2).
  const isDisabledDay = (date: Date): boolean => {
    if (date < today || date > latestBookableDay) {
      return true
    }
    const isoDate = iso(date)
    const meta = dayByIso.get(isoDate)
    if (!meta) {
      return true
    }
    if (isBookable(meta)) {
      if (slotStartOnly && !slotStartDates.has(isoDate)) {
        return true
      }
      if (checkoutBound) {
        if (date > checkoutBound.latest) {
          return true
        }
        if (fromDate && date > fromDate && date < checkoutBound.earliest) {
          return true
        }
      }
      return false
    }
    if (
      turnaroundEnabled &&
      fromDate &&
      !toDate &&
      checkoutOnly.has(isoDate) &&
      checkoutBound &&
      date >= checkoutBound.earliest &&
      date <= checkoutBound.latest
    ) {
      return false
    }
    return true
  }

  const disabled: Matcher[] = [isDisabledDay]

  const modifiers: Record<string, Matcher | Matcher[]> = {
    avail: statusDates.avail,
    low: statusDates.low,
    full: statusDates.full,
    checkoutOnly: [...checkoutOnly]
      .map((d) => toDateOrNull(d))
      .filter((d): d is Date => d != null),
    annotated: statusDates.annotated,
    seasonClosed: statusDates.seasonClosed,
    outsideWindow: statusDates.outsideWindow,
    leadTime: statusDates.leadTime,
    periodBandA: statusDates.periodBandA,
    periodBandB: statusDates.periodBandB,
    slotArrival: statusDates.slotArrival,
    slotMid: statusDates.slotMid,
  }
  const rangeEnd = toDate ?? previewTo
  Object.assign(modifiers, buildSelectionRangeModifiers(fromDate, rangeEnd))

  const onDayClick = (day: Date): void => {
    if (isDisabledDay(day)) {
      return
    }
    const clicked = iso(day)
    const meta = dayByIso.get(clicked)
    if (meta?.status === 'annotated') {
      onAnnotatedActivate?.(meta)
      return
    }
    if (meta?.status === 'season_closed') {
      onSeasonBlockActivate?.(meta)
      return
    }
    if (meta?.status === 'full') {
      onFullActivate?.(meta)
      return
    }
    if (isBlock) {
      onSelect({ from: clicked, to: iso(addDays(day, lockedNights ?? 0)) })
      return
    }
    if (slotStartOnly) {
      const checkout = slotStartCheckout.get(clicked)
      if (checkout) onSelect({ from: clicked, to: checkout })
      return
    }
    const ci = selectedFrom ?? ''
    const co = selectedTo ?? ''
    // Open a fresh range when none is pending, both ends are set, or the click is at/before the check-in.
    if (ci === '' || co !== '' || clicked <= ci) {
      onSelect({ from: clicked, to: null })
      return
    }
    // Different slot than check-in → start a new stay (cannot span availability periods).
    if (
      checkInSlotId != null &&
      meta?.slotId != null &&
      meta.slotId !== checkInSlotId
    ) {
      onSelect({ from: clicked, to: null })
      return
    }
    if (differenceInCalendarDays(parseISO(clicked), parseISO(ci)) > effectiveMaxSpanDays) {
      return
    }
    onSelect({ from: ci, to: clicked })
  }

  // Responsive: single month on mobile (bottom sheet), two months inline from `md` (>=768px).
  const [isDesktop, setIsDesktop] = useState(false)
  useEffect(() => {
    if (typeof window === 'undefined') return
    const mq = window.matchMedia('(min-width: 768px)')
    const apply = () => setIsDesktop(mq.matches)
    apply()
    mq.addEventListener('change', apply)
    return () => mq.removeEventListener('change', apply)
  }, [])

  const useSheet =
    mobileLayout === 'sheet' || (mobileLayout === 'hybrid' && !isDesktop)
  const multiMonth = isDesktop && mobileLayout !== 'sheet'

  // Notify the parent of the visible span so it can fetch availability for the shown months.
  const [visibleMonth, setVisibleMonth] = useState<Date>(() => startOfMonth(fromDate ?? today))
  const firstBookableMonth = startOfMonth(today)
  const lastBookableMonth = startOfMonth(latestBookableDay)
  const clampVisibleMonth = (month: Date): Date => {
    const m = startOfMonth(month)
    if (m < firstBookableMonth) return firstBookableMonth
    if (m > lastBookableMonth) return lastBookableMonth
    return m
  }
  const lastSpanRef = useRef('')
  useEffect(() => {
    const from = startOfMonth(visibleMonth)
    let to = endOfMonth(addDays(endOfMonth(visibleMonth), multiMonth ? 1 : 0))
    if (differenceInCalendarDays(to, from) > effectiveMaxSpanDays) {
      to = addDays(from, effectiveMaxSpanDays)
    }
    const key = `${iso(from)}_${iso(to)}`
    if (lastSpanRef.current === key) return
    lastSpanRef.current = key
    onRangeChange(from, to)
  }, [visibleMonth, multiMonth, onRangeChange, effectiveMaxSpanDays])

  const [sheetOpen, setSheetOpen] = useState(false)
  const checkInId = useId()
  const checkOutId = useId()

  const fmtShort = (d: Date): string => format(d, 'd MMM', { locale: dateFnsLocale() })
  // CAL-A3: explain the minimum stay while a check-in is open (NN/g: explain, don't just disable).
  const showMinStayHint = !isBlock && !!fromDate && !toDate && minNights > 1
  const minStayHintText = showMinStayHint ? labels.minStayHint(minNights) : ''
  const announce =
    fromDate && toDate
      ? labels.announceStay(fmtShort(fromDate), fmtShort(toDate), nights)
      : fromDate
        ? `${labels.announceCheckIn(fmtShort(fromDate))}${minStayHintText ? ` ${minStayHintText}` : ''}`
        : ''

  const triggerLabel =
    fromDate && toDate
      ? `${fmtShort(fromDate)} – ${fmtShort(toDate)}`
      : fromDate
        ? `${fmtShort(fromDate)} – …`
        : labels.sheetTrigger

  const dayPicker = (
    <DayMetaContext.Provider
      value={{
        byIso: dayByIso,
        from: selectedFrom ?? '',
        to: selectedTo ?? '',
        checkInAria: labels.checkInAria,
        checkOutAria: labels.checkOutAria,
        checkoutOnly,
        checkoutOnlyAria: labels.checkoutOnlyAria,
        price: effectivePrice,
        pricesEnabled,
        dayPriceAria: labels.dayPriceAria,
        priceSizeClass,
        slotStartOnly,
        slotStartDates,
        slotArrivalAria: labels.slotArrivalAria ?? '',
      }}
    >
      <DayPicker
        mode={undefined}
        locale={dateFnsLocale()}
        numberOfMonths={multiMonth ? 2 : 1}
        month={visibleMonth}
        onMonthChange={(month) => setVisibleMonth(clampVisibleMonth(month))}
        startMonth={firstBookableMonth}
        endMonth={lastBookableMonth}
        navLayout="around"
        onDayClick={onDayClick}
        onDayMouseEnter={(day) => {
          startTransition(() => {
            setHovered(day)
          })
        }}
        disabled={disabled}
        modifiers={modifiers}
        modifiersClassNames={modifierClassNames}
        components={dayComponents}
        showOutsideDays={false}
        className="w-full text-foreground"
        labels={{
          labelPrevious: () => labels.prevMonth ?? '',
          labelNext: () => labels.nextMonthNav ?? labels.nextMonth,
        }}
        classNames={{
          ...rdpClassNames,
          root: cn(rdpClassNames.root, 'w-full'),
          months: cn(
            rdpClassNames.months,
            'flex gap-[var(--cal-month-gap)]',
            // Match numberOfMonths (viewport `multiMonth`), not @md/cal container width -
            // the property detail column is often narrower than @md while still desktop.
            multiMonth ? 'flex-row flex-nowrap justify-center' : 'flex-col',
          ),
          month: cn(rdpClassNames.month, 'relative mx-auto w-full max-w-[22rem]'),
          month_grid: cn(rdpClassNames.month_grid, 'mx-auto w-full'),
          month_caption: cn(
            rdpClassNames.month_caption,
            'relative flex items-center justify-center px-2 py-1 text-center text-sm font-semibold text-foreground',
          ),
          nav: cn(rdpClassNames.nav),
          button_previous: cn(
            rdpClassNames.button_previous,
            'inline-flex size-11 min-h-11 min-w-11 items-center justify-center rounded-md text-foreground',
            'hover:bg-accent/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
          ),
          button_next: cn(
            rdpClassNames.button_next,
            'inline-flex size-11 min-h-11 min-w-11 items-center justify-center rounded-md text-foreground',
            'hover:bg-accent/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
          ),
          weekday: cn(rdpClassNames.weekday, 'py-1.5 text-xs font-medium text-[#4a5c58] opacity-100!'),
          chevron: cn(rdpClassNames.chevron, 'size-4 fill-foreground'),
          // Today = subtle ring only; selection (selEdge) keeps the filled primary look.
          today: cn(
            rdpClassNames.today,
            '[&>button]:font-medium [&>button]:ring-1 [&>button]:ring-inset [&>button]:ring-[color:var(--v2-accent)]/50',
          ),
          day: cn(rdpClassNames.day, 'p-[var(--cal-cell-gap)]'),
          day_button: cn(
            rdpClassNames.day_button,
            'flex size-full min-h-0 flex-col items-center justify-center gap-0.5 rounded-[var(--cal-tile-radius)] border border-transparent p-[var(--cal-tile-padding)] text-sm leading-none',
          ),
          disabled: cn(
            rdpClassNames.disabled,
            'opacity-100 [&>button]:cursor-not-allowed [&>button]:bg-[color:var(--cal-outside-window)] [&>button]:text-muted-foreground',
          ),
        }}
        styles={{ month_grid: { width: '100%' } }}
      />
    </DayMetaContext.Provider>
  )

  const legend = (
    <div className="mt-4 rounded-md border border-border bg-muted/40 p-4">
      <h3 className="m-0 text-sm font-semibold text-foreground">{labels.legendTitle}</h3>
      <ul className="mt-3 list-none space-y-2 p-0 text-sm text-muted-foreground">
        {availabilityLegendOrder.map((status) => (
          <li key={status} className="flex items-start gap-2">
            <span className={cn(legendSwatch[status])} aria-hidden />
            <span>{labels.legend(status)}</span>
          </li>
        ))}
        {hasSeasonClosed && labels.legendSeasonClosed ? (
          <li className="flex items-start gap-2">
            <span className={cn(legendSeasonClosedSwatch)} aria-hidden />
            <span>{labels.legendSeasonClosed}</span>
          </li>
        ) : null}
        {hasLeadTime && labels.legendLeadTime ? (
          <li className="flex items-start gap-2">
            <span className={cn(legendLeadTimeSwatch)} aria-hidden />
            <span>{labels.legendLeadTime}</span>
          </li>
        ) : null}
        {showPeriodBands && labels.legendPeriodA ? (
          <li className="flex items-start gap-2">
            <span className={cn(legendPeriodASwatch)} aria-hidden />
            <span>{labels.legendPeriodA}</span>
          </li>
        ) : null}
        {showPeriodBands && labels.legendPeriodB ? (
          <li className="flex items-start gap-2">
            <span className={cn(legendPeriodBSwatch)} aria-hidden />
            <span>{labels.legendPeriodB}</span>
          </li>
        ) : null}
        {slotStartOnly && labels.legendSlotArrival ? (
          <li className="flex items-start gap-2">
            <span className={cn(legendSlotArrivalSwatch)} aria-hidden />
            <span>{labels.legendSlotArrival}</span>
          </li>
        ) : null}
        {slotStartOnly && statusDates.slotMid.length > 0 && labels.legendSlotMid ? (
          <li className="flex items-start gap-2">
            <span className={cn(legendSlotMidSwatch)} aria-hidden />
            <span>{labels.legendSlotMid}</span>
          </li>
        ) : null}
        {turnaroundEnabled && labels.legendCheckoutOnly ? (
          <li className="flex items-start gap-2">
            <span
              className={cn(
                legendSwatchBase,
                'border-border [background-image:linear-gradient(135deg,transparent_46%,var(--color-border)_46%,var(--color-border)_54%,transparent_54%)]',
              )}
              aria-hidden
            />
            <span>{labels.legendCheckoutOnly}</span>
          </li>
        ) : null}
      </ul>
      {labels.legendHint ? <p className="mt-3 text-xs text-muted-foreground">{labels.legendHint}</p> : null}
    </div>
  )

  // CAL-A5: typed date inputs as a keyboard-first alternative to the calendar grid.
  const trySelectTyped = (from: string | null, to: string | null): void => {
    if (from) {
      const meta = dayByIso.get(from)
      const bookable = meta?.status === 'available' || meta?.status === 'low'
      if (!bookable) return
      if (slotStartOnly && !slotStartDates.has(from)) return
    }
    if (to && from && !slotStartOnly) {
      const fromMeta = dayByIso.get(from)
      const toMeta = dayByIso.get(to)
      if (
        fromMeta?.slotId != null &&
        toMeta?.slotId != null &&
        fromMeta.slotId !== toMeta.slotId &&
        !checkoutOnly.has(to)
      ) {
        return
      }
      const checkoutOk =
        checkoutOnly.has(to) ||
        (() => {
          const meta = dayByIso.get(to)
          return meta?.status === 'available' || meta?.status === 'low' || meta?.status === 'full'
        })()
      if (!checkoutOk) return
      if (differenceInCalendarDays(parseISO(to), parseISO(from)) < Math.max(1, minNights)) return
      if (differenceInCalendarDays(parseISO(to), parseISO(from)) > effectiveMaxSpanDays) return
    }
    onSelect({ from, to })
  }

  const typedInputs = (
    <div className="grid grid-cols-2 gap-3">
      <div className="flex flex-col gap-1.5">
        <Label htmlFor={checkInId} className="text-xs">
          {labels.checkInLabel}
        </Label>
        <FrenchDateInput
          id={checkInId}
          min={todayIso}
          value={selectedFrom ?? ''}
          onChange={(v) => {
            const autoTo = isBlock && v
              ? iso(addDays(parseISO(v), lockedNights ?? 0))
              : slotStartOnly && v
                ? (slotStartCheckout.get(v) ?? null)
                : (selectedTo ?? null)
            trySelectTyped(v || null, autoTo)
          }}
        />
      </div>
      <div className="flex flex-col gap-1.5">
        <Label htmlFor={checkOutId} className="text-xs">
          {labels.checkOutLabel}
        </Label>
        <FrenchDateInput
          id={checkOutId}
          min={selectedFrom || todayIso}
          value={selectedTo ?? ''}
          disabled={isBlock || slotStartOnly}
          onChange={(v) => trySelectTyped(selectedFrom ?? null, v || null)}
        />
      </div>
    </div>
  )

  // CAL-A3: visible minimum-stay hint while a check-in is open. RESA-F04: the dead-end
  // case (no valid check-out at all from this date) takes priority over the generic hint.
  const minStayNode =
    isCheckoutDeadEnd && labels.checkoutDeadEndHint ? (
      <p className="mt-2 text-xs font-medium text-destructive" role="alert">
        {labels.checkoutDeadEndHint(minNights)}
      </p>
    ) : showMinStayHint ? (
      <p className="mt-2 text-xs font-medium text-warning">{minStayHintText}</p>
    ) : null

  // RESA-F04: quick way back after navigating several months forward - hidden once the
  // visible month already is the current one.
  const todayButton =
    labels.todayButton && !isSameMonth(visibleMonth, today) ? (
      <Button type="button" variant="outline" size="sm" onClick={() => setVisibleMonth(clampVisibleMonth(today))}>
        {labels.todayButton}
      </Button>
    ) : null

  // CAL-A1: nights + total price for the selected stay.
  const nightsTotal = (
    <p className="text-sm font-medium text-foreground">
      {nights > 0 ? labels.nights(nights) : ' '}
      {nights > 0 && stayTotalLabel ? <span className="ml-2 font-normal text-muted-foreground">· {stayTotalLabel}</span> : null}
    </p>
  )

  const clearButton =
    (fromDate || toDate) && !isBlock ? (
      <button
        type="button"
        onClick={() => onSelect({ from: null, to: null })}
        className="rounded-md px-2 py-1 text-xs font-medium text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        {labels.clear}
      </button>
    ) : null

  const liveRegion = (
    <p className="sr-only" aria-live="polite">
      {announce}
    </p>
  )

  // CAL-A6: error / skeleton / empty-month states around the grid.
  const noBookable = !loading && !error && safeDays.length > 0 && !safeDays.some((d) => d.selectable)
  const showSkeleton = loading && safeDays.length === 0
  const calendarArea = error ? (
    <div className="rounded-md border border-border bg-muted/40 p-6 text-center">
      <p className="text-sm text-muted-foreground">{labels.loadError}</p>
      {onRetry ? (
        <Button type="button" variant="outline" size="sm" className="mt-3" onClick={onRetry}>
          {labels.retry}
        </Button>
      ) : null}
    </div>
  ) : showSkeleton ? (
    <Skeleton className="h-72 w-full rounded-md" aria-hidden />
  ) : (
    <>
      {dayPicker}
      {minStayNode}
      {noBookable ? (
        <div className="mt-3 flex flex-col items-center gap-2 text-center">
          <p className="text-sm text-muted-foreground">{labels.emptyMonth}</p>
          <Button type="button" variant="outline" size="sm" onClick={() => setVisibleMonth(clampVisibleMonth(addMonths(visibleMonth, 1)))}>
            {labels.nextMonth}
          </Button>
          {emptyAction}
        </div>
      ) : null}
    </>
  )

  // Mobile sheet when hybrid+narrow or forced sheet; otherwise inline calendar.
  if (useSheet) {
    return (
      <div
        className={cn('availability-range-picker @container/cal', className)}
        style={pickerStyle}
        onMouseLeave={() => {
          startTransition(() => {
            setHovered(null)
          })
        }}
      >
        {liveRegion}
        <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
          <SheetTrigger asChild>
            <button
              type="button"
              className="flex h-12 w-full items-center justify-between gap-2 rounded-md border border-input bg-transparent px-3 text-left text-sm hover:bg-accent/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <span className={cn('truncate', !fromDate && 'text-muted-foreground')}>{triggerLabel}</span>
              {nights > 0 ? (
                <span className="shrink-0 rounded-pill bg-[var(--v2-brand-soft)] px-2 py-0.5 text-xs font-medium text-primary">
                  {labels.nights(nights)}
                </span>
              ) : null}
            </button>
          </SheetTrigger>
          <SheetContent side="bottom" sheetCloseLabel={labels.sheetClose} className="max-h-[90dvh] overflow-y-auto">
            <SheetHeader>
              <SheetTitle>{labels.sheetTitle}</SheetTitle>
            </SheetHeader>
            <div className="flex items-center justify-between gap-3">
              <p className="text-sm text-muted-foreground">{labels.pickHint}</p>
              {todayButton}
            </div>
            {typedInputs}
            <div role="group" aria-label={ariaLabel} aria-busy={loading} className="flex flex-col items-center">
              {calendarArea}
            </div>
            {legend}
            <div className="sticky bottom-0 -mx-6 -mb-6 mt-2 flex items-center justify-between gap-3 border-t border-border bg-background px-6 py-3 pb-[max(0.75rem,env(safe-area-inset-bottom))]">
              {nightsTotal}
              <div className="flex items-center gap-2">
                {clearButton}
                <SheetClose asChild>
                  <Button type="button" size="sm">
                    {labels.sheetApply}
                  </Button>
                </SheetClose>
              </div>
            </div>
          </SheetContent>
        </Sheet>
      </div>
    )
  }

  // Desktop / inline: calendar in page flow.
  return (
    <div
      className={cn('availability-range-picker @container/cal', className)}
      style={pickerStyle}
      onMouseLeave={() => {
        startTransition(() => {
          setHovered(null)
        })
      }}
    >
      {liveRegion}
      <div className="mb-3 flex items-center justify-between gap-3">
        <p className="text-sm text-muted-foreground">{labels.pickHint}</p>
        {todayButton}
      </div>
      <div className="mb-3 max-w-md">{typedInputs}</div>
      <div role="group" aria-label={ariaLabel} aria-busy={loading}>
        {calendarArea}
      </div>
      <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
        {nightsTotal}
        {clearButton}
      </div>
      {legend}
    </div>
  )
}
